// One report tab (B10 S15; plan D3, D5–D8, D17; DC-210 "Reports is one page, seven tabs, one report shape.", DC-211
// "The three clocks are a legend on every report."; offer.md calls 16–17). Every tab has the same bands: the filter bar
// (date, his §7.2 filters as chips, Saved views, Group by, Sort, Export), the KPI strip of the tab's measures in his
// words, and the body — breakdown bars beside the drill-down — with the clocks legend at the top of the side column.
// Clicking a KPI or a bar narrows the drill-down to the rows that figure counted; a row opens its record.
//
// `ReportView` draws; `ReportTab` reads (`report.options`, `report.run`, `report.drill`, `report.export`) and owns the
// export. Every change of filter, grouping, sort, KPI, bar or segment is a new route (`spec.ts`), so the page is always
// a link. A measure whose producer has not merged draws "—" and is not drillable (the server refuses it, plan D2).

import { useState, type ReactNode } from "react";
import {
  Banner, Bars, Card, ErrorStrip, FilterBar, Flex, HEALTH_BAND_LABELS, Kpi, KpiRow, KpiSelect, LEVEL_DISPLAY,
  Legend, METRIC_LABELS, RULE_LABELS, Recorded, SLA_LABELS, SURFACE_LABELS, ACCESS_LABELS, Segmented, Select, Split, Stack,
  TextLink, formatDate, type BarItem, type FilterBarFilter, type Level, type MenuItem, type SelectOption,
} from "@/mc";
import {
  reportDrill, reportExport, reportOptions, reportRun, type DimensionOut, type MeasureOut, type ReportDrillOutput,
  type ReportExportOutput, type ReportOptionsOutput, type ReportRunOutput,
} from "@/api/tools";
import type { AppError } from "@/api/runtime";
import { errorText, useRead } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { SavedViewsSelect } from "@/views/b10/lib/savedViews";
import { workbenchHref } from "@/views/b10/workbench/query";
import { DrillTable } from "./DrillTable";
import {
  APPLICATION_SORT_LABELS, REPORT_MEASURE_WORDS, REPORT_PAGE_LABELS, SORT_LABELS, dimensionLabel, familyLabel, measureLabel,
} from "./labels";
import {
  PERIOD_PRESET_DAYS, drillInput, exportInput, periodEnding, reportHref, reportStateHref, rptHs, runInput, withFilter,
  type DrillState, type OwnFilter, type ReportFilterValue, type ReportQuery,
} from "./spec";

const NONE = "—";

// ---- presentation per family --------------------------------------------------------------------------------------

/** The measures a family draws as one breakdown (bars, his order) rather than as KPI tiles. */
const SERIES: Readonly<Record<string, (key: string) => boolean>> = {
  portfolio: (k) => ["on_hold", "cancelled", "reopened", "filled", "archived"].includes(k),
  executive: (k) => k.startsWith("health_"),
  pipeline: (k) => k.startsWith("stage_"),
  offer: (k) => ["extended", "accepted", "declined"].includes(k),
};

/** The bars' heading when they are a family's series. */
const SERIES_TITLE: Readonly<Record<string, string>> = {
  portfolio: REPORT_MEASURE_WORDS.statusSeries,
  executive: METRIC_LABELS.reqHealth,
  pipeline: SURFACE_LABELS.candidatesByStage,
  offer: REPORT_MEASURE_WORDS.offerSeries,
};

/* DC-17's band colour of a health bar (Green · Yellow (his gold) · Red) waits on a design-system route for a lane
 * screen to name a V2 fill; the token contract keeps V2 palette names inside src/mc, so the bars take
 * the default fill and the band's word on each bar carries the band. */

/** *Own metrics* / *Team view* (CR-044 "Own metrics; TA Manager team view"): the family's person dimension, the role
 *  whose own figures it shows, and the scope word that is only one's own (a reader whose scope says anything else has
 *  a team to view; the Recruiter's word is "Assigned/Operational" since DC-228). Recruiter operations and Coordinator
 *  operations only. */
const PERSON_FAMILY: Readonly<Record<string, { dimension: string; role: string; ownWord: string }>> = {
  recruiter_operations: { dimension: "recruiter", role: "recruiter", ownWord: ACCESS_LABELS["Assigned/Operational"] },
  coordinator_operations: { dimension: "coordinator", role: "coordinator", ownWord: ACCESS_LABELS.Operational },
};

export interface OwnSetup extends OwnFilter {
  canOwn: boolean;
  canTeam: boolean;
}

/** Which of *Own metrics* and *Team view* this reader has on this family; null where the family has neither. The
 *  person dimension may still wait for its producer (the Coordinator is B2's column): until `report.options` offers it,
 *  a filter or a grouping by it is refused (`dimension_unavailable`, `group_by_unavailable`), so the family has neither
 *  and draws its figures over the reader's scope as they are. *Team view* groups by the person, so it needs the
 *  dimension groupable too. */
export function ownSetup(
  family: string,
  options: Pick<ReportOptionsOutput, "scope_words" | "dimensions"> | undefined,
  me: { person_id: string; roles: readonly string[]; view_as?: { person_id: string } | null } | null | undefined,
): OwnSetup | null {
  const p = PERSON_FAMILY[family];
  if (!p || !options || !me) return null;
  const person = (options.dimensions ?? []).find((d) => d.key === p.dimension);
  if (!person) return null;
  const canOwn = me.roles.includes(p.role);
  const canTeam = person.groupable && options.scope_words.some((w) => w !== p.ownWord);
  if (!canOwn && !canTeam) return null;
  return { dimension: p.dimension, personId: me.view_as?.person_id ?? me.person_id, canOwn, canTeam };
}

/** The segment shown: the one the route asks for when the reader has it, else *Team view* when they have it, else
 *  *Own metrics*; undefined on a family without the two. A link asking for a view the reader lacks never reaches
 *  `report.run` as that view. */
export function viewOf(own: OwnSetup | null | undefined, asked: DrillState["view"]): DrillState["view"] {
  if (!own) return undefined;
  if (asked === "own" && own.canOwn) return "own";
  if (asked === "team" && own.canTeam) return "team";
  return own.canTeam ? "team" : "own";
}

/** A figure whose rows are another screen's queue rather than a report drill-down: S09's *Open requests* (the
 *  scheduling requests open now) opens the Coordinator Workbench filtered by the meeting type the report is filtered
 *  by, through S21's `workbenchHref`, which writes the Workbench's own query (C-B10-13; S09 D-S09-7). Only one meeting
 *  type is a Workbench filter: none, or two or more, open the whole queue. Every other figure drills on this page. */
export function elsewhereHref(family: string, measure: string, spec: ReportQuery): string | undefined {
  if (family !== "coordinator_operations" || measure !== "open_requests") return undefined;
  const types = (spec.filters ?? {})["meeting_type"];
  const type = Array.isArray(types) && types.length === 1 ? types[0] : null;
  return workbenchHref(type ? { type } : {});
}

/** A group's bar clicked: the drill-down lists that group's rows, and a second click clears it. A grouping the page made
 *  itself (*Team view* groups by the person) becomes the spec's own, so the group chosen rides in the link (a bucket
 *  without a grouping is never sent: `bucket_needs_group_by`). */
export function groupClick(
  spec: ReportQuery,
  drill: DrillState,
  groupedBy: string,
  value: string,
): { spec: ReportQuery; drill: DrillState } {
  return {
    spec: spec.group_by ? spec : { ...spec, group_by: groupedBy },
    drill: { ...drill, bucket: drill.bucket === value ? undefined : value },
  };
}

// ---- figures ----------------------------------------------------------------------------------------------------------

const round = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(1));
const numberOf = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

export interface Figure {
  value: string;
  unit?: string | undefined;
  sub?: ReactNode;
}

/** What a KPI tile prints for a measure: Active Time as the number of a duration with Calendar Time and Days on Hold
 *  beside it, a health measure's mean score with its bands, a rate as a percentage; "—" when not yet available. */
export function measureFigure(m: Pick<MeasureOut, "key" | "unit" | "status" | "value" | "detail">): Figure {
  if (m.status !== "available") return { value: NONE };
  const detail = m.detail ?? {};
  if (m.key === "health") {
    const mean = numberOf(detail.mean_score);
    const bands = (["green", "yellow", "red"] as const).map((band) => (
      <span key={band}>
        {band !== "green" ? " · " : null}
        {HEALTH_BAND_LABELS[band]}
        {" "}
        {numberOf(detail[band]) ?? 0}
      </span>
    ));
    return { value: mean === null ? NONE : round(mean), sub: <>{bands}</> };
  }
  const value = numberOf(m.value);
  if (m.unit === "days") {
    const active = numberOf(detail.median_active) ?? value;
    const calendar = numberOf(detail.median_calendar);
    const hold = numberOf(detail.mean_hold);
    const sub = calendar === null && hold === null ? undefined : (
      <>
        {calendar !== null ? <>{SLA_LABELS.calendarTime}{" "}{round(calendar)}</> : null}
        {calendar !== null && hold !== null ? " · " : null}
        {hold !== null ? <>{SLA_LABELS.daysOnHold}{" "}{round(hold)}</> : null}
      </>
    );
    return { value: active === null ? NONE : round(active), unit: active === null ? undefined : METRIC_LABELS.days, sub };
  }
  if (value === null) return { value: NONE };
  if (m.unit === "percent") return { value: `${round(value)}%` };
  return { value: round(value) };
}

/** His word for a stored value of a dimension: a level's display name, a health band, internal / external; else the
 *  server's label (a person's name, a reason's configured words) or the value itself. */
export function valueLabel(dimension: string, value: string, serverLabel?: string | null): string {
  if (dimension === "level" && value in LEVEL_DISPLAY) return LEVEL_DISPLAY[value as Level];
  if (dimension === "health" && value in HEALTH_BAND_LABELS) return HEALTH_BAND_LABELS[value as keyof typeof HEALTH_BAND_LABELS];
  if (value === "") return NONE;
  return serverLabel ?? value;
}

function chipValue(d: DimensionOut | undefined, key: string, value: ReportFilterValue): string {
  if (typeof value === "boolean") {
    return key === "internal_external" ? (value ? REPORT_MEASURE_WORDS.internal : REPORT_MEASURE_WORDS.external) : value ? "✓" : "✗";
  }
  if (Array.isArray(value)) {
    return value.map((v) => valueLabel(key, v, d?.values?.find((x) => x.value === v)?.label)).join(", ");
  }
  return `${value.at_least ?? ""}–${value.at_most ?? ""}`;
}

const periodKey = (p: { start: string; end: string }): string => `${p.start}..${p.end}`;
const periodWords = (p: { start: string; end: string }): string => `${formatDate(p.start, "UTC")} – ${formatDate(p.end, "UTC")}`;

// ---- the view -----------------------------------------------------------------------------------------------------

export interface ReportViewProps {
  family: string;
  spec: ReportQuery;
  drill: DrillState;
  options?: ReportOptionsOutput | undefined;
  run?: ReportRunOutput | undefined;
  rows?: ReportDrillOutput | undefined;
  loading?: { options?: boolean; run?: boolean; rows?: boolean } | undefined;
  errors?: { options?: AppError | null; run?: AppError | null; rows?: AppError | null; export?: AppError | null } | undefined;
  own?: OwnSetup | null | undefined;
  /** A change of filter, grouping, sort, figure or segment: the new route. */
  onNavigate: (href: string) => void;
  onExport?: (() => void) | undefined;
  exporting?: boolean | undefined;
  exported?: ReportExportOutput | null | undefined;
  /** The Saved views control (S19's select over `report:<family>`), drawn beside the filter bar. */
  savedViews?: ReactNode;
}

function problemOf(error: AppError) {
  return { status: error.status, refusal_code: error.code, detail: error.message, request_id: error.requestId };
}

export function ReportView({
  family, spec, drill, options, run, rows, loading, errors, own, onNavigate, onExport, exporting, exported, savedViews,
}: ReportViewProps) {
  const [adding, setAdding] = useState("");
  const go = (nextSpec: ReportQuery, nextDrill: DrillState = drill) => onNavigate(reportStateHref(family, nextSpec, nextDrill));
  const dims = options?.dimensions ?? [];
  const dimOf = (key: string) => dims.find((d) => d.key === key);
  const title = familyLabel(family) ?? family;
  const view = viewOf(own, drill.view);

  // ---- the filter bar ----
  const defaultPeriod = options?.period_default;
  const presets: SelectOption[] = [];
  if (defaultPeriod) {
    for (const days of PERIOD_PRESET_DAYS) {
      const p = periodEnding(defaultPeriod.end, days);
      presets.push({ value: periodKey(p), label: periodWords(p) });
    }
    if (spec.period && !presets.some((o) => o.value === periodKey(spec.period as { start: string; end: string }))) {
      presets.push({ value: periodKey(spec.period), label: periodWords(spec.period) });
    }
  }
  const periodValue = spec.period ? periodKey(spec.period) : defaultPeriod ? periodKey(defaultPeriod) : "";
  const onPeriod = (value: string) => {
    if (!value || (defaultPeriod && value === periodKey(defaultPeriod))) {
      const { period: _drop, ...rest } = spec;
      go(rest);
      return;
    }
    const [start, end] = value.split("..");
    if (start && end) go({ ...spec, period: { start, end } });
  };

  const chips: FilterBarFilter[] = Object.entries(spec.filters ?? {}).map(([key, value]) => ({
    name: dimensionLabel(key, dimOf(key)?.words),
    value: chipValue(dimOf(key), key, value as ReportFilterValue),
    onRemove: () => go(withFilter(spec, key, undefined), { ...drill, bucket: undefined }),
    hs: rptHs("chip", key),
  }));

  const groupable = dims.filter((d) => d.groupable && !d.period);
  const breakdowns = options?.breakdowns ?? [];
  const groupOptions: SelectOption[] = [
    ...breakdowns.flatMap((k) => (dimOf(k)?.groupable ? [{ value: k, label: dimensionLabel(k, dimOf(k)?.words) }] : [])),
    ...groupable.filter((d) => !breakdowns.includes(d.key)).map((d) => ({ value: d.key, label: dimensionLabel(d.key, d.words) })),
  ];
  const sortWords = rows?.subject === "application" ? APPLICATION_SORT_LABELS : SORT_LABELS;
  const sortOptions: SelectOption[] = (options?.sorts ?? []).flatMap((k) => {
    const word = sortWords[k];
    return word ? [{ value: k, label: `${word} ↑` }, { value: `-${k}`, label: `${word} ↓` }] : [];
  });
  const sortValue = spec.sort ? `${spec.sort.descending ? "-" : ""}${spec.sort.by}` : "";
  // While a file is being written the item stays, and a second choice is ignored (one receipt per file asked for).
  const exportItems: MenuItem[] = onExport
    ? [{ label: REPORT_PAGE_LABELS.csv, onSelect: () => { if (!exporting) onExport(); }, hs: rptHs("export", "csv") }]
    : [];

  // ---- the filter picker (his §7.2 filters, each opening its select) ----
  const pickable = dims.filter((d) => !d.period && (d.op === "in" || d.op === "bool") && (d.op === "bool" || (d.values ?? []).length > 0));
  const picking = adding ? dimOf(adding) : undefined;
  const pickValues: SelectOption[] = picking
    ? picking.op === "bool"
      ? [{ value: "1", label: adding === "internal_external" ? REPORT_MEASURE_WORDS.internal : "✓" },
        { value: "0", label: adding === "internal_external" ? REPORT_MEASURE_WORDS.external : "✗" }]
      : (picking.values ?? []).map((v) => ({ value: v.value, label: valueLabel(picking.key, v.value, v.label) }))
    : [];
  const onPickValue = (value: string) => {
    if (!picking || value === "") return;
    const current = (spec.filters ?? {})[picking.key];
    const next: ReportFilterValue = picking.op === "bool"
      ? value === "1"
      : [...new Set([...(Array.isArray(current) ? current : []), value])];
    setAdding("");
    go(withFilter(spec, picking.key, next), { ...drill, bucket: undefined });
  };

  // ---- figures ----
  const measures: readonly MeasureOut[] = run?.measures
    ?? (options?.measures ?? []).map((m) => ({ key: m.key, label_key: m.label_key, unit: m.unit, status: m.status }));
  const inSeries = SERIES[family] ?? (() => false);
  const tiles = measures.filter((m) => !inSeries(m.key));
  const series = measures.filter((m) => inSeries(m.key));
  const wordOf = (m: Pick<MeasureOut, "label_key">) => measureLabel(m.label_key) ?? title;
  const toggleMeasure = (key: string) => go(spec, { ...drill, measure: drill.measure === key ? undefined : key });

  // ---- the bars: the groups when grouped, else the family's series ----
  const groups = run?.groups ?? [];
  const groupedBy = run?.group_by ?? undefined;
  const focus = drill.measure ? measures.find((m) => m.key === drill.measure && m.status === "available") : undefined;
  let bars: BarItem[] = [];
  let barsTitle: string | null = null;
  if (groupedBy && groups.length > 0) {
    const valueOf = (g: (typeof groups)[number]): number => {
      const m = focus ? g.measures.find((x) => x.key === focus.key) : undefined;
      return focus ? numberOf(m?.value) ?? 0 : g.count;
    };
    const max = Math.max(1, ...groups.map(valueOf));
    bars = groups.map((g) => ({
      label: (
        <TextLink onClick={() => { const next = groupClick(spec, drill, groupedBy, g.value); go(next.spec, next.drill); }} hs={rptHs("bar", "group")}>
          {valueLabel(groupedBy, g.value, g.label)}
        </TextLink>
      ),
      pct: (100 * valueOf(g)) / max,
      value: focus ? measureFigure(g.measures.find((x) => x.key === focus.key) ?? { ...focus, status: "not_yet_available" }).value : String(g.count),
    }));
    barsTitle = dimensionLabel(groupedBy, dimOf(groupedBy)?.words);
  } else if (series.length > 0) {
    const max = Math.max(1, ...series.map((m) => numberOf(m.value) ?? 0));
    bars = series.map((m) => ({
      label: m.status === "available"
        ? <TextLink onClick={() => toggleMeasure(m.key)} hs={rptHs("bar", m.key)}>{wordOf(m)}</TextLink>
        : wordOf(m),
      pct: (100 * (numberOf(m.value) ?? 0)) / max,
      value: measureFigure(m).value,
    }));
    barsTitle = SERIES_TITLE[family] ?? title;
  }

  const legend = (
    <Legend
      items={[
        { tone: "neutral", label: SLA_LABELS.calendarTime },
        { tone: "info", label: SLA_LABELS.activeTime },
        { tone: "warn", label: SLA_LABELS.daysOnHold },
      ]}
    >
      {RULE_LABELS.slaUsesActiveTime}
    </Legend>
  );

  const kpis = (
    <KpiRow>
      {tiles.map((m) => {
        const label = wordOf(m);
        if (loading?.run && !run) return <Kpi key={m.key} label={label} loading />;
        const f = measureFigure(m);
        if (m.status !== "available") return <Kpi key={m.key} label={label} value={f.value} hs={rptHs("kpi", m.key)} />;
        const away = elsewhereHref(family, m.key, spec);
        if (away) return <Kpi key={m.key} label={label} value={f.value} unit={f.unit} sub={f.sub} href={away} hs={rptHs("kpi", m.key)} />;
        return (
          <KpiSelect
            key={m.key}
            label={label}
            value={f.value}
            unit={f.unit}
            sub={f.sub}
            pressed={drill.measure === m.key}
            onPressedChange={() => toggleMeasure(m.key)}
            hs={rptHs("kpi", m.key)}
          />
        );
      })}
    </KpiRow>
  );

  const segment = own && own.canOwn && own.canTeam ? (
    <Segmented
      label={title}
      value={view ?? "team"}
      options={[
        { value: "own", label: REPORT_MEASURE_WORDS.ownMetrics, hs: rptHs("segment", "own") },
        { value: "team", label: REPORT_MEASURE_WORDS.teamView, hs: rptHs("segment", "team") },
      ]}
      onChange={(v) => go(spec, { ...drill, view: v === "own" || v === "team" ? v : undefined, bucket: undefined })}
    />
  ) : null;
  const segmentWord = own && !(own.canOwn && own.canTeam)
    ? own.canOwn ? REPORT_MEASURE_WORDS.ownMetrics : REPORT_MEASURE_WORDS.teamView
    : null;

  const total = rows ? (rows.subject === "application" ? rows.total + (rows.aggregated ?? 0) : rows.total) : undefined;

  if (errors?.options) {
    return <ErrorStrip message={errorText(errors.options)} problem={problemOf(errors.options)} />;
  }

  return (
    <Stack>
      <Flex wrap justify="between">
        <FilterBar
          label={title}
          date={presets.length ? { options: presets, value: periodValue, onChange: onPeriod, hs: rptHs("date") } : undefined}
          filters={chips}
          groupBy={groupOptions.length ? { options: groupOptions, value: spec.group_by ?? "", onChange: (v) => go({ ...spec, group_by: v || null }, { ...drill, bucket: undefined }), hs: rptHs("group") } : undefined}
          sort={sortOptions.length ? {
            options: sortOptions,
            value: sortValue,
            onChange: (v) => go({ ...spec, sort: v ? { by: v.replace(/^-/, ""), descending: v.startsWith("-") } : null }),
            hs: rptHs("sort"),
          } : undefined}
          exportItems={exportItems}
          exportHs={rptHs("export")}
        />
        {savedViews}
      </Flex>
      {pickable.length > 0 ? (
        <Flex wrap>
          <Select
            sm
            aria-label={REPORT_PAGE_LABELS.filters}
            placeholder={REPORT_PAGE_LABELS.filters}
            options={pickable.map((d) => ({ value: d.key, label: dimensionLabel(d.key, d.words) }))}
            value={adding}
            onChange={setAdding}
            hs={rptHs("filter", "add")}
          />
          {picking ? (
            <Select
              sm
              aria-label={dimensionLabel(picking.key, picking.words)}
              options={pickValues}
              value=""
              onChange={onPickValue}
              hs={rptHs("filter", "value")}
            />
          ) : null}
        </Flex>
      ) : null}
      {segment ?? (segmentWord ? <Banner tone="info">{segmentWord}</Banner> : null)}
      {errors?.run ? <ErrorStrip message={errorText(errors.run)} problem={problemOf(errors.run)} /> : null}
      {errors?.export ? <ErrorStrip message={errorText(errors.export)} problem={problemOf(errors.export)} /> : null}
      {exported ? (
        <Recorded live right={exported.filename}>
          {exported.rows}
          {exported.dropped_columns.length > 0 ? <>{" · "}{REPORT_PAGE_LABELS.restrictedFieldsDoNotLeak}{" "}{exported.dropped_columns.join(", ")}</> : null}
        </Recorded>
      ) : null}
      <Split
        main={
          <Stack>
            {kpis}
            <Card title={REPORT_PAGE_LABELS.drillDown} count={total} sub={(rows?.scope_words ?? options?.scope_words ?? []).join(" · ")} flush>
              {errors?.rows ? <ErrorStrip message={errorText(errors.rows)} problem={problemOf(errors.rows)} /> : <DrillTable data={rows} loading={loading?.rows} />}
            </Card>
          </Stack>
        }
        side={
          <Stack>
            {legend}
            {barsTitle ? (
              <Card title={barsTitle}>
                <Bars items={bars} wrapl />
              </Card>
            ) : null}
          </Stack>
        }
      />
    </Stack>
  );
}

// ---- the container ------------------------------------------------------------------------------------------------

function saveCsv(out: ReportExportOutput): void {
  const url = URL.createObjectURL(new Blob([out.csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = out.filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export interface ReportTabProps {
  family: string;
  spec: ReportQuery;
  drill: DrillState;
  onNavigate: (href: string) => void;
}

/** A report tab with its reads: the family's options, its run over the spec, the drill-down, and the export. */
export function ReportTab({ family, spec, drill, onNavigate }: ReportTabProps) {
  const { me } = useSession();
  const options = useRead(["report", "options", family], () => reportOptions({ family }));
  const own = ownSetup(family, options.data, me);
  const view = viewOf(own, drill.view);
  const state: DrillState = { ...drill, view };
  const ownFilter = own ? { dimension: own.dimension, personId: own.personId } : undefined;
  const runIn = runInput(family, spec, state, ownFilter);
  const run = useRead(["report", "run", runIn], () => reportRun(runIn), { enabled: !!options.data });
  const available = new Set((options.data?.measures ?? []).filter((m) => m.status === "available").map((m) => m.key));
  const drillIn = drillInput(family, spec, state, { sorts: options.data?.sorts, drillable: (m) => available.has(m), own: ownFilter });
  const rows = useRead(["report", "drill", drillIn], () => reportDrill(drillIn), { enabled: !!options.data });
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState<ReportExportOutput | null>(null);
  const [exportError, setExportError] = useState<AppError | null>(null);
  const onExport = () => {
    setExporting(true);
    setExportError(null);
    reportExport(exportInput(drillIn))
      .then((out) => {
        saveCsv(out);
        setExported(out);
      })
      .catch((e: AppError) => setExportError(e))
      .finally(() => setExporting(false));
  };
  const current: Record<string, unknown> = { ...spec, family };
  return (
    <ReportView
      family={family}
      spec={spec}
      drill={state}
      options={options.data}
      run={run.data}
      rows={rows.data}
      loading={{ options: options.isLoading, run: run.isLoading, rows: rows.isLoading }}
      errors={{ options: options.error, run: run.error, rows: rows.error, export: exportError }}
      own={own}
      onNavigate={onNavigate}
      onExport={onExport}
      exporting={exporting}
      exported={exported}
      savedViews={
        <SavedViewsSelect
          surface={`report:${family}`}
          current={current}
          onApply={(saved) => onNavigate(reportHref(family, saved as ReportQuery))}
          hs={rptHs("views")}
        />
      }
    />
  );
}
